/**
 * Admin Roles - Users with these roles bypass all permission checks
 * Admin users have unrestricted access to all features and actions
 */
export const ADMIN_ROLES = ['super_admin', 'Admin'] as const

/**
 * Permission Groups
 * Organized by module/resource for easy rendering of checkbox groups in UI
 * Each group contains a list of related permissions with display names
 */
export const PERMISSION_GROUPS = [
  {
    name: 'restaurant',
    displayName: 'Restaurant Management',
    permissions: [
      { key: 'RESTAURANT_LIST', value: 'restaurant:list', displayName: 'List Restaurants' },
      { key: 'RESTAURANT_CREATE', value: 'restaurant:create', displayName: 'Create Restaurant' },
      { key: 'RESTAURANT_BASIC_INFO_VIEW', value: 'restaurant-basic-info:view', displayName: 'View Basic Information' },
      { key: 'RESTAURANT_BASIC_INFO_EDIT', value: 'restaurant-basic-info:edit', displayName: 'Edit Basic Information' },
      { key: 'RESTAURANT_HOURS_VIEW', value: 'restaurant-hours:view', displayName: 'View Opening Hours' },
      { key: 'RESTAURANT_HOURS_EDIT', value: 'restaurant-hours:edit', displayName: 'Edit Opening Hours' },
      { key: 'RESTAURANT_PAYMENT_VIEW', value: 'restaurant-payment:view', displayName: 'View Payment Settings' },
      { key: 'RESTAURANT_PAYMENT_EDIT', value: 'restaurant-payment:edit', displayName: 'Edit Payment Settings' },
      { key: 'RESTAURANT_MATRIX_VIEW', value: 'restaurant-matrix:view', displayName: 'View Matrix Interface' },
      { key: 'RESTAURANT_MATRIX_EDIT', value: 'restaurant-matrix:edit', displayName: 'Edit Matrix Interface' },
      { key: 'RESTAURANT_MENU_ITEMS_VIEW', value: 'restaurant-menu-items:view', displayName: 'View Menu Items' },
      { key: 'RESTAURANT_MENU_ITEMS_CREATE', value: 'restaurant-menu-items:create', displayName: 'Create Menu Items' },
      { key: 'RESTAURANT_MENU_ITEMS_EDIT', value: 'restaurant-menu-items:edit', displayName: 'Edit Menu Items' },
      { key: 'RESTAURANT_MENU_ITEMS_DELETE', value: 'restaurant-menu-items:delete', displayName: 'Delete Menu Items' },
      { key: 'RESTAURANT_SETTINGS_VIEW', value: 'restaurant-settings:view', displayName: 'View Settings' },
      { key: 'RESTAURANT_SETTINGS_EDIT', value: 'restaurant-settings:edit', displayName: 'Edit Settings' },
      { key: 'RESTAURANT_ORDER_MODES_VIEW', value: 'restaurant-order-modes:view', displayName: 'View Order Modes' },
      { key: 'RESTAURANT_ORDER_MODES_EDIT', value: 'restaurant-order-modes:edit', displayName: 'Edit Order Modes' },
      { key: 'RESTAURANT_RECEPTION_ACCOUNTS_VIEW', value: 'restaurant-reception-accounts:view', displayName: 'View Reception Account' },
      { key: 'RESTAURANT_RECEPTION_ACCOUNTS_CREATE', value: 'restaurant-reception-accounts:create', displayName: 'Create Reception Account' },
      { key: 'RESTAURANT_RECEPTION_ACCOUNTS_EDIT', value: 'restaurant-reception-accounts:edit', displayName: 'Edit Reception Account' },
      { key: 'RESTAURANT_PICKUP_MONITORS_VIEW', value: 'restaurant-pickup-monitors:view', displayName: 'View Pickup Monitor' },
      { key: 'RESTAURANT_PICKUP_MONITORS_CREATE', value: 'restaurant-pickup-monitors:create', displayName: 'Create Pickup Monitor' },
      { key: 'RESTAURANT_PICKUP_MONITORS_EDIT', value: 'restaurant-pickup-monitors:edit', displayName: 'Edit Pickup Monitor' },
      { key: 'RESTAURANT_SOT_ACCOUNTS_VIEW', value: 'restaurant-sot-accounts:view', displayName: 'View SOT Account' },
      { key: 'RESTAURANT_SOT_ACCOUNTS_CREATE', value: 'restaurant-sot-accounts:create', displayName: 'Create SOT Account' },
      { key: 'RESTAURANT_SOT_ACCOUNTS_EDIT', value: 'restaurant-sot-accounts:edit', displayName: 'Edit SOT Account' },
      { key: 'RESTAURANT_SOT_ACCOUNTS_DELETE', value: 'restaurant-sot-accounts:delete', displayName: 'Delete SOT Account' },
      { key: 'RESTAURANT_SERVICE_AREAS_VIEW', value: 'restaurant-service-areas:view', displayName: 'View Service Area' },
      { key: 'RESTAURANT_SERVICE_AREAS_CREATE', value: 'restaurant-service-areas:create', displayName: 'Create Service Area' },
      { key: 'RESTAURANT_SERVICE_AREAS_EDIT', value: 'restaurant-service-areas:edit', displayName: 'Edit Service Area' },
      { key: 'RESTAURANT_SERVICE_AREAS_DELETE', value: 'restaurant-service-areas:delete', displayName: 'Delete Service Area' },
      { key: 'RESTAURANT_PRINTERS_VIEW', value: 'restaurant-printers:view', displayName: 'View Printer' },
      { key: 'RESTAURANT_PRINTERS_CREATE', value: 'restaurant-printers:create', displayName: 'Create Printer' },
      { key: 'RESTAURANT_PRINTERS_EDIT', value: 'restaurant-printers:edit', displayName: 'Edit Printer' },
      { key: 'RESTAURANT_PRINTERS_DELETE', value: 'restaurant-printers:delete', displayName: 'Delete Printer' },
      { key: 'RESTAURANT_ORDERS_VIEW', value: 'restaurant-orders:view', displayName: 'View Orders' },
      { key: 'RESTAURANT_ORDERS_RESYNC', value: 'restaurant-orders:resync', displayName: 'Resync Orders' },
      { key: 'RESTAURANT_ORDERS_PAYMENT_ACTION', value: 'restaurant-orders:payment-action', displayName: 'Payment Action' },
    ],
  },
  {
    name: 'role',
    displayName: 'Role & Permission Management',
    permissions: [
      { key: 'ROLE_LIST', value: 'role:list', displayName: 'List Roles' },
      { key: 'ROLE_CREATE', value: 'role:create', displayName: 'Create Role' },
      { key: 'ROLE_EDIT', value: 'role:edit', displayName: 'Edit Role' },
      { key: 'ROLE_DELETE', value: 'role:delete', displayName: 'Delete Role' },
    ],
  },
  {
    name: 'partners',
    displayName: 'Partners',
    permissions: [
      { key: 'PARTNERS_LIST', value: 'partners:list', displayName: 'List Partners' },
      { key: 'PARTNERS_CREATE', value: 'partners:create', displayName: 'Create Partners' },
      { key: 'PARTNERS_EDIT', value: 'partners:edit', displayName: 'Edit Partners' },
      { key: 'PARTNERS_DELETE', value: 'partners:delete', displayName: 'Delete Partners' },
    ],
  },
  {
    name: 'companies',
    displayName: 'Companies',
    permissions: [
      { key: 'COMPANIES_LIST', value: 'companies:list', displayName: 'List Companies' },
      { key: 'COMPANIES_CREATE', value: 'companies:create', displayName: 'Create Companies' },
      { key: 'COMPANIES_EDIT', value: 'companies:edit', displayName: 'Edit Companies' },
      { key: 'COMPANIES_DELETE', value: 'companies:delete', displayName: 'Delete Companies' },
    ],
  },
  {
    name: 'users',
    displayName: 'Users',
    permissions: [
      { key: 'USERS_LIST', value: 'users:list', displayName: 'List Users' },
      { key: 'USERS_CREATE', value: 'users:create', displayName: 'Create Users' },
      { key: 'USERS_EDIT', value: 'users:edit', displayName: 'Edit Users' },
      { key: 'USERS_DELETE', value: 'users:delete', displayName: 'Delete Users' },
    ],
  },
  {
    /*
     * The group name has to stay 'datev': syncRolePermissions stores it as the
     * permission's `type`, and firstOrCreate matches on name+guard+type. With a
     * different name here it would miss the seeded rows and try to insert a
     * second permission with the same name, which the unique index on
     * (name, guard_name) refuses.
     */
    name: 'datev',
    displayName: 'DATEV',
    permissions: [
      { key: 'COMPANY_DATEV_VIEW', value: 'company-datev:view', displayName: 'View DATEV Connection' },
      { key: 'COMPANY_DATEV_MANAGE', value: 'company-datev:manage', displayName: 'Manage DATEV Connection' },
      { key: 'COMPANY_DATEV_SUBMISSIONS_VIEW', value: 'company-datev-submissions:view', displayName: 'View DATEV Submissions' },
      { key: 'COMPANY_DATEV_SUBMISSIONS_DOWNLOAD', value: 'company-datev-submissions:download', displayName: 'Download DATEV Submission Files' },
      { key: 'RESTAURANT_DATEV_EXPORT_VIEW', value: 'restaurant-datev-export:view', displayName: 'View DATEV Export Settings and Export' },
      { key: 'RESTAURANT_DATEV_EXPORT_EDIT', value: 'restaurant-datev-export:edit', displayName: 'Edit DATEV Export Settings' },
    ],
  },
  {
    name: 'licenses',
    displayName: 'Licenses',
    permissions: [
      { key: 'LICENSES_LIST', value: 'licenses:list', displayName: 'View Licenses' },
      { key: 'LICENSES_ADD', value: 'licenses:add', displayName: 'Buy License' },
      { key: 'LICENSES_DELETE', value: 'licenses:delete',   displayName: 'Cancel License' },
      { key: 'LICENSES_PAYMENT_ADD', value: 'licenses:payment-add', displayName: 'Add Payment Method' },
      { key: 'LICENSES_PAYMENT_EDIT', value: 'licenses:payment-edit', displayName: 'Set Default Payment Method' },
      { key: 'LICENSES_PAYMENT_DELETE', value: 'licenses:payment-delete', displayName: 'Delete Payment Method' },
    ],
  },
] as const


/**
 * Types for permission group structure
 */
export interface PermissionItem {
  key: string
  value: string
  displayName: string
}

export interface PermissionGroupItem {
  name: string
  displayName: string
  permissions: PermissionItem[]
}

export type Permission = string
